import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  listAccounts,
  deleteAccount,
  createAccount,
  type Account,
  type CreateAccountData,
} from '../../api/accounts';
import { DataTable, type Column } from '../common/DataTable';
import { Modal } from '../common/Modal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { StatusBadge } from '../common/StatusBadge';
import { AccountForm } from './AccountForm';

/**
 * Format a date string for display, or return a dash if unavailable.
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
}

export function AccountsView() {
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Account | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listAccounts(token);
      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleAdd = async (data: CreateAccountData) => {
    const token = await getJwtToken();
    if (!token) return;
    await createAccount(data, token);
    setShowAddModal(false);
    setSuccess(`Account "${data.accountName}" added successfully.`);
    await fetchAccounts();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await deleteAccount(deleteTarget.accountId, token);
      setDeleteTarget(null);
      setSuccess(`Account "${deleteTarget.accountName}" deleted.`);
      await fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      setDeleteTarget(null);
    }
  };

  const handleRowClick = (account: Account) => {
    navigate(`/admin/accounts/${account.accountId}`);
  };

  const columns: Column<Account>[] = [
    {
      key: 'accountName',
      header: 'Name',
      render: (a) => <strong>{a.accountName}</strong>,
    },
    {
      key: 'accountId',
      header: 'Account ID',
      render: (a) => <code>{a.accountId}</code>,
    },
    {
      key: 'region',
      header: 'Region',
      render: (a) => <span>{a.region}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusBadge status={a.status} />,
    },
    {
      key: 'lastTestedAt',
      header: 'Last Tested',
      render: (a) => formatDate(a.lastTestedAt),
    },
    {
      key: 'models',
      header: 'Models',
      render: (a) => <span>{a.models?.length ?? 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (a) => (
        <button
          className="settings-card__delete"
          onClick={(ev) => {
            ev.stopPropagation();
            setDeleteTarget(a);
          }}
          type="button"
          title="Delete account"
        >
          x
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Accounts</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add Account
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchAccounts}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} type="button">
            Dismiss
          </button>
        </div>
      )}

      {loading && accounts.length === 0 ? (
        <div className="view__empty">Loading accounts...</div>
      ) : (
        <DataTable<Account>
          columns={columns}
          data={accounts}
          onRowClick={handleRowClick}
          keyExtractor={(a) => a.accountId}
          emptyMessage="No accounts found. Add an account to get started."
        />
      )}

      {/* Add Account Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Account"
        wide
      >
        <AccountForm
          onSubmit={handleAdd}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Account"
        message={`Are you sure you want to delete account "${deleteTarget?.accountName}" (${deleteTarget?.accountId})? This will not remove any model deployments using this account.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
